import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useStrings } from "../LanguageContext";
import "./Login.css";

const API_BASE = `${process.env.REACT_APP_API_URL}/api/auth`;

// Same rules the server enforces (AuthController.passwordProblem).
function passwordProblem(pw) {
  if (!pw || pw.length < 8) return "Password must be at least 8 characters long.";
  if (!/[A-Z]/.test(pw)) return "Password must contain at least one uppercase letter.";
  if (!/[0-9]/.test(pw)) return "Password must contain at least one number.";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Password must contain at least one special character.";
  return "";
}

/**
 * /reset-password?token=… — the page the emailed reset link opens. The token works once
 * and expires after 60 minutes; the server checks both.
 */
export default function ResetPassword() {
  const strings = useStrings();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const problem = passwordProblem(password);
    if (problem) { setError(problem); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/reset-password/confirm`, { token, newPassword: password });
      setDone(res.data?.message || "Your password has been changed. You can now sign in.");
    } catch (err) {
      setError(err.response?.data?.error || "Could not reset your password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-welcome">
          <h1>{strings.login.brand}</h1>
        </div>
        <h2>Choose a new password</h2>

        {!token ? (
          <div className="auth-form">
            <div className="auth-error" role="alert">This reset link is incomplete. Please open the link from your email again, or request a new one.</div>
            <Link to="/Login" className="auth-btn auth-btn-primary">Back to sign in</Link>
          </div>
        ) : done ? (
          <div className="auth-form">
            <div className="auth-success" role="status">{done}</div>
            <Link to="/Login" className="auth-btn auth-btn-primary">Sign in</Link>
          </div>
        ) : (
          <form className="auth-form" onSubmit={submit} noValidate>
            {error && <div className="auth-error" role="alert">{error}</div>}
            <p className="auth-forgot-info">
              Use at least 8 characters, with an uppercase letter, a number and a special character.
            </p>
            <div className="auth-field">
              <label htmlFor="reset-password">New password</label>
              <input id="reset-password" type="password" value={password} autoComplete="new-password"
                onChange={(e) => { setPassword(e.target.value); setError(""); }} />
            </div>
            <div className="auth-field">
              <label htmlFor="reset-confirm">Confirm new password</label>
              <input id="reset-confirm" type="password" value={confirm} autoComplete="new-password"
                onChange={(e) => { setConfirm(e.target.value); setError(""); }} />
            </div>
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? "Saving…" : "Save new password"}
            </button>
            <p className="auth-forgot-help">
              Link expired or already used? <Link to="/Login">Request a new one</Link> from "Forgot password".
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
